import React, {useEffect, useReducer, useState} from 'react';
import {ScrollView, View} from 'react-native';
import {
  HomeHeader,
  HomeNotes,
  HomeSession,
  HomePatients,
  HomeTodaysSessions,
} from 'src/modules/home/components';
import {BaseScreen} from 'src/design-system';
import {useDimensions} from '@react-native-community/hooks';
import {compose} from 'recompose';
import {withObservables} from '@nozbe/watermelondb/react';
import {withDatabase} from '@nozbe/watermelondb/react';
import {useDatabase} from '@nozbe/watermelondb/react';
import {of} from 'rxjs';
import withState from 'src/redux/wrapper';
import {rrulestr} from 'rrule';
import {endOfDay, startOfDay} from 'date-fns';
import DeleteSessionModal from 'src/modules/scheduling/components/delete-session-modal';
import {useStyle} from 'src/providers/style';
import {Appointment, Session} from 'src/models';
import moment from 'moment';
import {Q} from '@nozbe/watermelondb';
import {useSessionDateSync} from 'src/hooks/useSessionDateSync';
import Spinner from 'react-native-loading-spinner-overlay';

const HomeScreen = ({
  profile,
  patients,
  appointments,
  recurringAppointments,
  role,
  sessions,
  userNotesDue,
  authentication,
  assignedClients,
}: any) => {
  const dimensions = useDimensions();
  const styles = useStyle();
  const database = useDatabase();

  const [update, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const [showDelete, setShowDelete] = useState<string | boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [notesDueCount, setNotesDueCount] = useState<number>(0);
  const [notesDueTodayCount, setNotesDueTodayCount] = useState<number>(0);
  const [todayCompletedCount, setTodayCompletedCount] = useState<number>(0);
  const [inProgressCount, setInProgressCount] = useState<number>(0);
  const [notStartedCount, setNotStartedCount] = useState<number>(0);
  const [completedCount, setCompletedCount] = useState<number>(0);
  const {isLoading, syncDateRange} = useSessionDateSync();

  useEffect(() => {
    const oneMonthAgo = startOfDay(moment().subtract(1, 'months').toDate());
    const today = endOfDay(new Date());
    syncDateRange(oneMonthAgo, today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstName = profile?.firstName || '';

  const isSmallSize = dimensions.window.width < 768;

  const [todayAppointments, setTodayAppointments] = useState([]);

  const calcNotesDue = async () => {
    let notesCount = 0;
    let notesTodayCount = 0;
    let _completedCount = 0;
    const appointmentIds = todayAppointments?.map(
      (appointment: Appointment) => appointment?.id,
    );
    const sortedSessions = sessions.filter(
      (session: Session) =>
        session.sessionDate === moment(new Date()).format('YYYY-MM-DD'),
    );
    for (const session of sortedSessions) {
      if (!appointmentIds.includes(session.appointmentId)) {
        continue;
      }

      const currentStaffParticipant = await session
        .currentStaffParticipant(profile?.id)
        .fetch();
      if (currentStaffParticipant?.length > 0) {
        const isSupervision = currentStaffParticipant[0]?.supervision;
        if (isSupervision && session.type === 'supervision') {
          if (
            session?.startTimestamp &&
            session?.endTimestamp &&
            !session?.submissionTimestamp
          ) {
            notesCount++;
            if (
              session.startTimestamp > startOfDay(new Date()) &&
              session.endTimestamp < endOfDay(new Date())
            ) {
              notesTodayCount++;
            }
          }
          if (
            session?.submissionTimestamp &&
            session?.startTimestamp &&
            session?.endTimestamp &&
            session?.startTimestamp > startOfDay(new Date()) &&
            session?.endTimestamp < endOfDay(new Date())
          ) {
            _completedCount++;
          }
        } else if (!isSupervision && session.type === 'session') {
          if (
            session?.startTimestamp &&
            session?.endTimestamp &&
            !session?.submissionTimestamp
          ) {
            notesCount++;
            if (
              session.startTimestamp > startOfDay(new Date()) &&
              session.endTimestamp < endOfDay(new Date())
            ) {
              notesTodayCount++;
            }
          }
          if (
            session?.submissionTimestamp &&
            session?.startTimestamp &&
            session?.endTimestamp &&
            session?.startTimestamp > startOfDay(new Date()) &&
            session?.endTimestamp < endOfDay(new Date())
          ) {
            _completedCount++;
          }
        }
      }
    }
    setNotesDueCount(notesCount);
    setNotesDueTodayCount(notesTodayCount);
    setTodayCompletedCount(_completedCount);
  };

  useEffect(() => {
    calcNotesDue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayAppointments]);

  // Calculate session counts matching staff history screen logic
  useEffect(() => {
    const calculateSessionCounts = async () => {
      if (!profile?.id || !authentication?.selectedGroup) {
        return;
      }

      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      const today = new Date();

      // Build patient filter queries based on RBAC
      let patientQueries: any[] = [];
      if (role?.patientAssignedOnly) {
        const assignedClientIds = assignedClients?.map(
          (client: any) => client.id,
        );
        if (assignedClientIds && assignedClientIds.length > 0) {
          patientQueries = [Q.where('patient_id', Q.oneOf(assignedClientIds))];
        } else {
          // If patientAssignedOnly but no assigned clients, no sessions should be shown
          setInProgressCount(0);
          setNotStartedCount(0);
          setCompletedCount(0);
          return;
        }
      }

      // Get all appointments where user is a participant, filtered by date range
      const userAppointments = await database
        .get(Appointment.table)
        .query(
          Q.where('_partition', authentication.selectedGroup),
          Q.or(
            Q.and(
              Q.where('rrule', ''),
              Q.where('date', Q.gt(startOfDay(oneMonthAgo).getTime())),
              Q.where('date', Q.lte(endOfDay(today).getTime())),
              Q.where('deleted_at', null),
            ),
            Q.and(
              Q.where('rrule', Q.notEq('')),
              Q.where('deleted_at', null),
              Q.or(
                Q.and(
                  Q.where(
                    'start_timestamp',
                    Q.gte(startOfDay(oneMonthAgo).getTime()),
                  ),
                  Q.where('start_timestamp', Q.lt(today.getTime())),
                ),
                Q.and(
                  Q.where(
                    'start_timestamp',
                    Q.lte(startOfDay(oneMonthAgo).getTime()),
                  ),
                  Q.or(
                    Q.where('end_timestamp', Q.eq(0)),
                    Q.where(
                      'end_timestamp',
                      Q.gte(startOfDay(oneMonthAgo).getTime()),
                    ),
                  ),
                ),
              ),
            ),
          ),
          Q.on(
            'participants',
            Q.and(Q.where('user_id', profile.id), Q.where('deleted_at', null)),
          ),
        )
        .fetch();

      const sessionsQueried: any[] = [];

      for (const appointment of userAppointments) {
        const appt = appointment as any;
        const staffParticipants = await appt.staffParticipants.fetch();
        const userParticipant = staffParticipants.find(
          (p: any) => p.userId === profile.id,
        );

        if (!userParticipant) {
          continue;
        }

        // Determine session type based on participant's supervision status
        const sessionType = userParticipant.supervision
          ? 'supervision'
          : 'session';

        if (appt.rrule) {
          // Handle recurring appointments
          const rruleSet = rrulestr(appt.rrule, {forceset: true});
          const appointmentEndDate = endOfDay(new Date());
          const appointmentStartDate = startOfDay(oneMonthAgo);

          const startTime = new Date(
            Date.UTC(
              appointmentStartDate.getUTCFullYear(),
              appointmentStartDate.getUTCMonth(),
              appointmentStartDate.getUTCDate(),
              0,
              0,
              0,
            ),
          );
          const endTime = new Date(
            Date.UTC(
              appointmentEndDate.getUTCFullYear(),
              appointmentEndDate.getUTCMonth(),
              appointmentEndDate.getUTCDate(),
              23,
              59,
              59,
            ),
          );

          const allOccurrences = rruleSet
            .between(startTime, endTime, true)
            .map((rruleDate: Date) => {
              const realDate = new Date(
                rruleDate.getFullYear(),
                rruleDate.getMonth(),
                rruleDate.getDate(),
              );
              realDate.setTime(
                rruleDate.getTime() +
                  rruleSet.options.dtstart.getTimezoneOffset() * 60 * 1000,
              );
              return realDate;
            });

          const dayAfter = new Date(
            appointmentEndDate.getFullYear(),
            appointmentEndDate.getMonth(),
            appointmentEndDate.getDate() + 1,
            0,
            0,
            0,
          );

          const dayBefore = new Date(
            appointmentStartDate.getFullYear(),
            appointmentStartDate.getMonth(),
            appointmentStartDate.getDate() - 1,
            0,
            0,
            0,
          );

          const excludedDates = new Set(
            (rruleSet as any)
              .exdates()
              .map((d: Date) => d.toISOString().split('T')[0]),
          );

          for (const date of allOccurrences) {
            if (excludedDates.has(date.toISOString().split('T')[0])) {
              continue;
            }
            if (
              date.toISOString().split('T')[0] ===
              dayAfter.toISOString().split('T')[0]
            ) {
              continue;
            }
            if (
              date.toISOString().split('T')[0] ===
              dayBefore.toISOString().split('T')[0]
            ) {
              continue;
            }

            const apptSessions = await database
              .get(Session.table)
              .query(
                Q.where('appointment_id', appt.id),
                Q.where('session_date', moment(date).format('YYYY-MM-DD')),
                Q.where('type', sessionType),
                Q.where('deleted_at', null),
                ...patientQueries,
                Q.sortBy('end_timestamp', Q.desc),
              )
              .fetch();

            if (apptSessions[0]) {
              sessionsQueried.push(apptSessions[0]);
            }
          }
        } else {
          // Non-recurring appointment
          const apptSessions = await database
            .get(Session.table)
            .query(
              Q.where('appointment_id', appt.id),
              Q.where('session_date', moment(appt.date).format('YYYY-MM-DD')),
              Q.where('type', sessionType),
              Q.where('deleted_at', null),
              ...patientQueries,
              Q.sortBy('end_timestamp', Q.desc),
            )
            .fetch();

          if (apptSessions.length > 0) {
            sessionsQueried.push(apptSessions[0]);
          }
        }
      }

      // Count by status - matching staff history screen status definitions
      let inProgress = 0;
      let notStarted = 0;
      let completed = 0;

      for (const session of sessionsQueried) {
        // not-started: start_timestamp is null
        if (!session.startTimestamp) {
          notStarted++;
        }
        // in-progress: has start_timestamp but no end_timestamp
        else if (session.startTimestamp && !session.endTimestamp) {
          inProgress++;
        }
        // completed: has submission_timestamp
        else if (session.submissionTimestamp) {
          completed++;
        }
        // Note: sessions with endTimestamp but no submissionTimestamp are "missing-values"
        // which is the same as userNotesDue - we don't count those here
      }

      setInProgressCount(inProgress);
      setNotStartedCount(notStarted);
      setCompletedCount(completed);
    };

    if (!isLoading) {
      calculateSessionCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profile?.id,
    authentication?.selectedGroup,
    sessions,
    isLoading,
    assignedClients,
  ]);

  useEffect(() => {
    const curateAppointments = async () => {
      const curatedAppointments = [];
      for (const recurringAppointment of recurringAppointments) {
        const rrule = rrulestr(recurringAppointment.rrule, {
          forceset: true,
        });

        // Accommodate Daylight Savings
        let appointmentEndDate = endOfDay(new Date());
        let appointmentStartDate = startOfDay(new Date());

        const startTime = new Date(
          Date.UTC(
            appointmentStartDate.getUTCFullYear(),
            appointmentStartDate.getUTCMonth(),
            appointmentStartDate.getUTCDate(),
            0,
            0,
            0,
          ),
        );
        const endTime = new Date(
          Date.UTC(
            appointmentEndDate.getUTCFullYear(),
            appointmentEndDate.getUTCMonth(),
            appointmentEndDate.getUTCDate(),
            23,
            59,
            59,
          ),
        );

        const allOccurrences = rrule
          .between(startTime, endTime, true)
          .map(rruleDate => {
            const realDate = new Date(
              rruleDate.getFullYear(),
              rruleDate.getMonth(),
              rruleDate.getDate(),
            );
            realDate.setTime(
              rruleDate.getTime() +
                rrule.options.dtstart.getTimezoneOffset() * 60 * 1000,
            );
            return realDate;
          });

        const excludedDates = new Set(
          rrule.exdates().map(d => d.toISOString().split('T')[0]),
        );
        const todaysDate = new Date();
        for (const date of allOccurrences) {
          if (excludedDates.has(date.toISOString().split('T')[0])) {
            continue;
          }
          if (
            date.toISOString().split('T')[0] !==
            new Date(
              todaysDate.setTime(
                todaysDate.getTime() -
                  todaysDate.getTimezoneOffset() * 60 * 1000,
              ),
            )
              .toISOString()
              .split('T')[0]
          ) {
            continue;
          }
          const appointmentPatients =
            await recurringAppointment.patients.fetch();
          const patient = appointmentPatients?.[0];
          if (
            (role?.patientAssignedOnly && assignedClients.includes(patient)) ||
            !role?.patientAssignedOnly
          ) {
            curatedAppointments.push(recurringAppointment);
          }
        }
      }
      for (const appointment of appointments) {
        const appointmentPatients = await appointment.patients.fetch();
        const patient = appointmentPatients?.[0];
        if (
          (role?.patientAssignedOnly && assignedClients.includes(patient)) ||
          !role?.patientAssignedOnly
        ) {
          curatedAppointments.push(appointment);
        }
      }

      setTodayAppointments(curatedAppointments);
    };
    if (!isLoading) {
      curateAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, recurringAppointments, update, isLoading]);

  const deleteCallback = (item: any, supervision: boolean, dtSession: any) => {
    setShowDelete({
      appointment: item,
      date: new Date(),
      supervision,
      dtSession,
    });
  };
  const deleteCancelled = () => {
    setShowDelete(false);
    forceUpdate();
  };

  return (
    <BaseScreen header={false}>
      <Spinner visible={isLoading} />
      <DeleteSessionModal
        show={[showDelete, setShowDelete]}
        deleteCancelled={() => deleteCancelled()}
      />
      <ScrollView>
        <HomeHeader name={firstName} />
        <View style={[styles.container, styles.flex, styles.marginVertical]}>
          {isSmallSize ? (
            <>
              <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                <HomeSession
                  total={todayAppointments?.length || 0}
                  completed={todayCompletedCount}
                  sessionNotesDue={notesDueTodayCount}
                  role={role}
                />
              </View>
              <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                <HomeNotes
                  notesDue={userNotesDue?.length}
                  inProgress={inProgressCount}
                  completed={completedCount}
                  notStarted={notStartedCount}
                />
              </View>
              <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                <HomeTodaysSessions
                  profile={profile}
                  appointments={todayAppointments}
                  canEdit={role?.appointmentEdit}
                  canDelete={role?.appointmentDelete}
                  deleteCallback={deleteCallback}
                />
              </View>
              <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                <HomePatients patients={patients} />
              </View>
            </>
          ) : (
            <>
              <View style={[styles.flex13]}>
                <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                  <HomeSession
                    total={todayAppointments?.length || 0}
                    completed={todayCompletedCount}
                    sessionNotesDue={notesDueTodayCount}
                    role={role}
                  />
                </View>
                <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                  <HomeTodaysSessions
                    profile={profile}
                    appointments={todayAppointments}
                    canEdit={role?.appointmentEdit}
                    canDelete={role?.appointmentDelete}
                    deleteCallback={deleteCallback}
                    canView={role?.appointmentView}
                  />
                </View>
              </View>
              <View style={[styles.flex]}>
                <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                  <HomeNotes
                    notesDue={userNotesDue?.length}
                    inProgress={inProgressCount}
                    completed={completedCount}
                    notStarted={notStartedCount}
                  />
                </View>
                <View style={[styles.paddingHorizontal, styles.paddingBottom]}>
                  <HomePatients
                    patients={patients}
                    canView={!role?.patientAssignedOnly}
                  />
                </View>
              </View>
            </>
          )}
        </View>
      </ScrollView>
    </BaseScreen>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables(['authentication'], ({database, authentication}: any) => {
    return {
      profile: authentication?.userId
        ? database.get('users').findAndObserve(authentication.userId)
        : of(),
    };
  }),
  withObservables(['authentication'], ({profile, authentication}: any) => {
    return {
      role: profile.role,
      authentication: of(authentication),
      sessions: profile.userSessions(authentication.selectedGroup),
      patients: profile.patients,
    };
  }),
  withObservables([], ({profile, authentication, role, patients}: any) => {
    const oneMonthAgo = startOfDay(moment().subtract(1, 'months').toDate());

    // Build patient filter queries based on RBAC
    let patientQueries: any[] = [];
    if (role?.patientAssignedOnly) {
      const clientIds = patients.length
        ? patients.map((pat: any) => pat.id)
        : [];
      if (clientIds.length > 0) {
        patientQueries = [Q.where('patient_id', Q.oneOf(clientIds))];
      }
    }

    return {
      userNotesDue: profile.database
        .get(Session.table)
        .query(
          Q.where('user_id', profile?.id),
          Q.where('start_timestamp', Q.notEq(null)),
          Q.where('session_date', Q.notEq(null)),
          Q.where(
            'session_date',
            Q.gte(moment(oneMonthAgo).format('YYYY-MM-DD')),
          ),
          Q.where('end_timestamp', Q.notEq(null)),
          Q.where('submission_timestamp', Q.eq(null)),
          Q.where('deleted_at', null),
          Q.where('_partition', authentication.selectedGroup),
          ...patientQueries,
        ),
    };
  }),
  withObservables([], ({database, profile}: any) => {
    const todaysDate = new Date();
    return {
      appointments: database
        .get('appointments')
        .query(
          Q.on(
            'participants',
            Q.and(
              Q.where('user_id', Q.eq(profile.id)),
              Q.where('deleted_at', null),
            ),
          ),
          Q.and(
            Q.where('rrule', Q.eq('')),
            Q.where('type', Q.notEq('event')),
            Q.where('date', Q.gte(startOfDay(todaysDate).getTime())),
            Q.where('date', Q.lt(endOfDay(todaysDate).getTime())),
            Q.where('deleted_at', Q.eq(null)),
          ),
          Q.sortBy('start', Q.asc),
        ),
      recurringAppointments: database
        .get('appointments')
        .query(
          Q.and(
            Q.where('rrule', Q.notEq('')),
            Q.where('type', Q.notEq('event')),
            Q.where('deleted_at', Q.eq(null)),
            Q.or(
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.gt(startOfDay(todaysDate).getTime()),
                ),
                Q.where(
                  'start_timestamp',
                  Q.lt(endOfDay(todaysDate).getTime()),
                ),
              ),
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.lt(startOfDay(todaysDate).getTime()),
                ),
                Q.or(
                  Q.where('end_timestamp', Q.eq(0)),
                  Q.where(
                    'end_timestamp',
                    Q.gt(startOfDay(todaysDate).getTime()),
                  ),
                ),
              ),
            ),
          ),
          Q.on(
            'participants',
            Q.and(
              Q.where('user_id', Q.eq(profile.id)),
              Q.where('deleted_at', null),
            ),
          ),
          Q.sortBy('start', Q.asc),
        ),
    };
  }),
  withObservables([], ({role, profile}: any) => {
    if (role?.patientAssignedOnly) {
      return {
        assignedClients: profile.patients,
      };
    }
    return {
      assignedClients: of([]),
    };
  }),
)(HomeScreen);
